<script>
import Timer from '@components/timer/timer.vue';
import { workoutComputed, workoutMethods } from '@state/helpers';

export default {
  components: {
    Timer,
  },
  props: {
    width: {
      type: Number,
      required: true,
      default: 0,
    },
  },
  data() {
    return {
      restIntervalId: null,
      restTime: 0,
      starting: true,
    };
  },
  computed: {
    ...workoutComputed,
    remainingSets() {
      return this.currentExercise && this.currentSetIndex && this.currentExercise.setDirections.length - this.currentSetIndex;
    },
    hasTabataFitnessType() {
      if (this.currentCircuit.fitness_types) {
        return this.currentCircuit.fitness_types.filter((e) => e.label.toLowerCase() === 'tabata').length > 0;
      } else {
        return false;
      }
    },
    isTabata() {
      if (this.hasTabataFitnessType) {
        return this.currentCircuit.tabata || this.hasTabataFitnessType;
      } else {
        return this.currentCircuit.tabata;
      }
    },
  },
  mounted() {
    this.starting = true;
    this.restTime = this.afterSetRest;
    this.restIntervalId = setInterval(() => {
      if (this.restTime <= 0) {
        this.onSkipRestPeriod();
      } else {
        this.restTime--;
        this.starting = false;
      }
    }, 1000);
  },
  destroyed() {
    clearInterval(this.restIntervalId);
  },
  methods: {
    ...workoutMethods,
    onSkipRestPeriod() {
      this.restTime = 0;
      this.starting = false;
      this.setSkipRestPeriod(true);
    },
  },
};
</script>

<template>
  <div class="rest-details-section">
    <div class="up-next">
      <p>Up Next:</p>
      <p v-if="restType === 'circuit'">
        <span v-if="nextCircuit">{{ nextCircuit.label }}</span>
        <span v-else>End Workout</span>
      </p>
      <p v-else>{{ currentExercise.label }}</p>
      <p>
        <strong>
          <span v-if="restType === 'circuit' && nextCircuit">{{ nextCircuit.trainerFeedback.intro.text }}</span>
          <span v-if="restType === 'exercise'">
            <span class="remaining-sets">
              <strong v-if="currentExercise && currentExercise.setDirections.length"
                >{{ remainingSets }} set<span v-if="remainingSets !== 1">s</span> left.
              </strong>
            </span>
            <template v-if="currentExercise.requiredEquipment.unweighted.length">
              Grab your
              <span v-for="(equipment, i) in currentExercise.requiredEquipment.unweighted" :key="i">
                {{ equipment.label }}
                <span
                  v-if="i + 1 < currentExercise.requiredEquipment.unweighted.length || currentExercise.requiredEquipment.weighted"
                  >,</span
                >
              </span>
            </template>
            <span v-if="currentExercise.requiredEquipment.weighted">{{ currentExercise.requiredEquipment.weighted.label }}</span>
          </span>
        </strong>
      </p>
    </div>
    <div class="reps">
      <Timer
        :key="afterSetRest"
        :restCountDown="restTime"
        :type="'rest'"
        :isClockwise="true"
        :diameter="width"
        :timeRemaining="starting ? 0 : afterSetRest - restTime"
        :originalTime="afterSetRest"
        :backgroundColor="isTabata ? 'rgba(255, 255, 255, 0.1)' : 'rgb(115, 115, 115)'"
        :startColor="isTabata ? '#FFA9AC' : '#3F3F3F'"
        :stopColor="isTabata ? '#C93D42' : '#3F3F3F'"
        :tabata="isTabata"
        :lineWidth="13"
      />
    </div>
    <p class="skip-rest-period" @click="onSkipRestPeriod">
      Skip
    </p>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
.tabata-circuit {
  .rest-wrapper {
    background: #e12128;
  }
}
.rest-details-section {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;

  .up-next {
    color: white;
    display: block;
    margin-bottom: 4rem;
    text-align: center;
    width: 90%;

    @media screen and (min-width: 1100px) {
      width: 50%;
    }

    p {
      margin-bottom: 0.5rem;
      margin-left: auto;
      margin-right: auto;

      &:first-child {
        color: rgba(white, 0.8);
        font-family: $proxima-nova;
        font-size: 15px;
        font-style: normal;
        font-weight: 600;
        line-height: 15px;

        @media screen and (min-width: 1100px) {
          @include calc-font-size(1);
        }
      }

      &:nth-child(2) {
        color: white;
        font-family: $roboto;
        font-size: 24px;
        font-style: normal;
        font-weight: bold;
        letter-spacing: 0.5px;
        line-height: 4rem;
        margin: 1rem 0 2rem;
        overflow: hidden;
        position: relative;
        text-overflow: ellipsis;
        white-space: nowrap;

        @media screen and (min-width: 1100px) {
          @include calc-font-size(2.9);
        }
      }

      &:nth-child(3) {
        color: rgba(#f2f2f2, 0.7);
        font-family: $proxima-nova;
        font-size: 1.8rem;
        font-style: normal;
        font-weight: 300;
        line-height: 1.7rem;
        mix-blend-mode: normal;
        text-transform: capitalize;

        @media screen and (min-width: 1100px) {
          @include calc-font-size(1);
        }

        strong {
          color: rgba(white, 0.8);
          font-family: $proxima-nova;
          font-size: 15px;
          font-style: normal;
          font-weight: 600;
          line-height: 15px;

          @media screen and (min-width: 1100px) {
            @include calc-font-size(1);
          }
        }
      }

      .remaining-sets {
        text-transform: lowercase;
      }
    }
  }

  .skip-rest-period {
    color: rgba(white, 0.8);
    font-family: $proxima-nova;
    font-size: 1.6rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.5rem;

    &:hover {
      cursor: pointer;
    }
  }
}
</style>
